import React, { useMemo, useState } from 'react';

import TimelineFilterChips from './TimelineFilterChips';
import PatientTimelineYearGroup from './PatientTimelineYearGroup';
import { SECTION_STATUS, TIMELINE_KINDS, TIMELINE_TYPE_FILTER } from '../../constants/patientCard';
import { ROUTES } from '../../constants/routes';
import { isProceduresTruncated } from '../../utils/patientCardProcedures';
import { printPatientCard } from '../../utils/printPatientCard';
import {
  readLane,
  laneFailureNotices,
  laneFailureHeading,
  NOTICE_LANES,
  FAILED_NOTICE_BODY,
  NOT_BUILT_HEADING,
  NOT_BUILT_BODY,
  COUNT_UNKNOWN,
} from '../../utils/patientCardLanes';

/**
 * PatientTimeline (F6) — one reverse-chronological stream merging OPD visits
 * and IPD admissions (mockup Variant A, owner-chosen), driven by the §1.2
 * `sections.timeline` refs.
 *
 * Props:
 *   sections — the FULL `sections` map from the §1.2 clinical-record
 *     envelope (`GET .../clinical-record`). Reads `sections.timeline` for
 *     the ordered ref list and `sections.visits` / `sections.admissions` to
 *     resolve each ref's display fields. This component NEVER fetches —
 *     the one aggregator call already happened at the page level (F5); it is
 *     pure presentation over already-loaded data.
 *   generatedAt — the envelope's own `generated_at` (F10 — the print
 *     header's as-of stamp; this component is the only place both the
 *     resolved timeline rows AND the active type filter already live, so
 *     the "Print card" trigger sits here rather than being lifted to the
 *     page — see `printPatientCard.js`).
 *   loading — true while the page's single aggregator call is in flight;
 *     renders a skeleton instead of reading `sections` (which may be stale
 *     or absent during that window).
 *   onRetry — the page's own memoized aggregator refetch (Q2). Fired ONLY
 *     by an explicit click on a lane-failure notice's `Try again`; the page
 *     swaps to its loading state on that click, so the control cannot be
 *     re-clicked while the request is in flight and one click can only ever
 *     produce one request. The card has no per-section endpoint, so the
 *     retry re-reads the whole card (PO Q20's fallback) — never a per-row
 *     or per-lane fan-out.
 *
 * `sections` is also passed down through `PatientTimelineYearGroup` to each
 * `TimelineRow`, which hands it to the real expanded-detail components
 * (F7 `OpdTimelineRow` / F8 `IpdTimelineRow`) — see `TimelineRow.jsx` for the
 * row slot / prop interface and for where a row's expansion state lives.
 */
export default function PatientTimeline({ sections, generatedAt, loading = false, onRetry }) {
  const [typeFilter, setTypeFilter] = useState(TIMELINE_TYPE_FILTER.ALL);

  const timelineSection = sections?.timeline;
  const admissionsSection = sections?.admissions;
  // Never a client-side flag lookup (build-plan F6) — the IPD chip's
  // existence is decided from the SAME section-status the server sends for
  // `sections.admissions`, nothing re-derived locally.
  const ipdEnabled = admissionsSection?.status !== SECTION_STATUS.MODULE_DISABLED;

  // bk-819 (F7-F) — ONE boolean, shared with every `OpdTimelineRow`'s empty
  // line (`isProceduresTruncated`), so the card-level notice and the row
  // lines can never disagree about whether procedures are fully loaded.
  const proceduresTruncated = isProceduresTruncated(sections?.procedures);

  // Q2 (QA D3) — the notices for lanes the server could NOT read. Derived
  // from the ONE shared guard, in the contract's canonical section order:
  // one notice per failed lane, collapsing to a single notice at three.
  // A `NOT_IMPLEMENTED` lane never appears here (a retry could not succeed);
  // it renders its own no-retry copy inside the section body instead.
  const failureNotices = laneFailureNotices(sections, NOTICE_LANES);

  // A count for a lane we could not read renders `—`, never `0` — a `0`
  // re-asserts the absence the notice just denied (§24). OPD rows are backed
  // by `visits`, IPD rows by `admissions`; if either is unreadable the
  // combined "All" count is unknown too.
  const visitsUnreadable = readLane(sections?.visits).unavailable;
  const admissionsUnreadable = readLane(admissionsSection).unavailable;

  // Resolve + sort ONCE — every consumer below (chip counts, year groups)
  // reads from this SAME array, so a header count can never drift from the
  // rows actually rendered.
  const rows = useMemo(() => resolveTimelineRows(timelineSection, sections), [timelineSection, sections]);

  const { filteredRows, counts } = useMemo(() => {
    const opd = rows.filter((r) => r.bucket === 'OPD');
    const ipd = rows.filter((r) => r.bucket === 'IPD');
    const byFilter = {
      [TIMELINE_TYPE_FILTER.ALL]: rows,
      [TIMELINE_TYPE_FILTER.OPD]: opd,
      [TIMELINE_TYPE_FILTER.IPD]: ipd,
    };
    return {
      filteredRows: byFilter[typeFilter] || rows,
      counts: {
        ALL: (visitsUnreadable || admissionsUnreadable) ? COUNT_UNKNOWN : rows.length,
        OPD: visitsUnreadable ? COUNT_UNKNOWN : opd.length,
        IPD: admissionsUnreadable ? COUNT_UNKNOWN : ipd.length,
      },
    };
  }, [rows, typeFilter, visitsUnreadable, admissionsUnreadable]);

  const yearGroups = useMemo(() => groupByYear(filteredRows), [filteredRows]);

  if (loading) {
    return (
      <div data-testid="timeline-skeleton" className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 rounded-md bg-slate-100 dark:bg-slate-800 animate-pulse" />
        ))}
      </div>
    );
  }

  // Q2 — the timeline lane's OWN failure runs through the same guard and the
  // same notice as every other lane: `NOT_IMPLEMENTED` gets the no-retry
  // copy, a genuine read failure gets the notice + `Try again`.
  const timelineLane = readLane(timelineSection);
  if (!timelineSection || timelineLane.unavailable) {
    return (
      <div data-testid="timeline-unavailable" className="py-3">
        {timelineLane.notBuilt ? (
          <NotBuiltNotice testId="timeline" />
        ) : (
          <FailureNotice
            notice={{ key: 'timeline', heading: laneFailureHeading('timeline'), body: FAILED_NOTICE_BODY }}
            onRetry={onRetry}
          />
        )}
      </div>
    );
  }

  // Section notices render ABOVE the rows (and above the empty state) — an
  // inline caveat under forty visit rows is invisible (long-list rule).
  const noticesBlock = failureNotices.map((notice) => (
    <FailureNotice key={notice.key} notice={notice} onRetry={onRetry} />
  ));

  if (rows.length === 0) {
    return (
      <div>
        {noticesBlock}
        {/* The empty state asserts an absence about visits AND admissions —
            it may only render while BOTH of those lanes were actually read.
            Otherwise the notice above carries the truth on its own (§29). */}
        {!visitsUnreadable && !admissionsUnreadable && (
          <div data-testid="timeline-empty" className="text-center py-8">
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">No visits or admissions recorded</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Nothing has been recorded for this patient yet.
            </p>
          </div>
        )}
      </div>
    );
  }

  const activeChipLabel = typeFilter === TIMELINE_TYPE_FILTER.OPD
    ? 'OPD'
    : typeFilter === TIMELINE_TYPE_FILTER.IPD ? 'IPD' : 'All';

  // F10 — client-side print of exactly what's already resolved here: the
  // SAME `yearGroups` the screen renders from (uncapped — the 20/50 reveal
  // limit is an on-screen-only affordance, print includes every row), the
  // SAME `typeFilter` currently active, and the SAME `proceduresTruncated`
  // boolean driving the notice above. Zero network requests — see
  // `printPatientCard.js` for the static proof this can't reach a document
  // endpoint.
  const handlePrint = () => {
    printPatientCard({
      patient: sections?.patient?.item || null,
      generatedAt,
      typeFilter,
      yearGroups,
      proceduresTruncated,
      documentsSection: sections?.documents,
    });
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={handlePrint}
          data-testid="patient-card-print-button"
          className="rounded-md border border-slate-400 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          🖨️ Print card
        </button>
      </div>
      {noticesBlock}
      {proceduresTruncated && (
        // bk-819 (F7-F) — card-level notice, rendered ONCE at the top of the
        // timeline region (never per row — a truncated plan's sittings can
        // belong to ANY visit row, so no single row can be marked). Info
        // treatment only: nothing is broken, no retry.
        <div
          data-testid="procedures-truncated-notice"
          className="mb-3 rounded-md border border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-900/30 px-3 py-2.5"
        >
          <p className="flex items-center gap-1.5 text-xs font-bold text-sky-900 dark:text-sky-100">
            <span aria-hidden="true">ℹ️</span>
            Not all procedures are shown
          </p>
          <p className="mt-1 text-[11px] text-sky-800 dark:text-sky-200">
            This patient has a very large number of treatment plans, so this page shows the most
            recent ones. All of their plans are listed on the{' '}
            <a href={ROUTES.PATIENT_PLANS} className="font-semibold underline">
              Patient plans
            </a>{' '}
            screen.
          </p>
        </div>
      )}
      <div className="mb-3">
        <TimelineFilterChips
          activeFilter={typeFilter}
          onChange={setTypeFilter}
          counts={counts}
          showIpd={ipdEnabled}
        />
      </div>
      {filteredRows.length === 0 ? (
        <div data-testid="timeline-filter-empty" className="text-center py-6 text-sm text-slate-500 dark:text-slate-400">
          No records match the &quot;{activeChipLabel}&quot; filter.
        </div>
      ) : (
        yearGroups.map(([year, yearRows], idx) => (
          // Keyed on `typeFilter` too: switching the chip changes WHICH year
          // is "newest" in the filtered view, so the group must remount with
          // a fresh `defaultOpen` rather than reusing a stale open/closed
          // state left over from the previous filter's year groups.
          <PatientTimelineYearGroup
            key={`${typeFilter}-${year}`}
            year={year}
            rows={yearRows}
            defaultOpen={idx === 0}
            sections={sections}
          />
        ))
      )}
    </div>
  );
}

// ── lane-state notices (Q2 / QA D3) ─────────────────────────────────────

/**
 * One section's "couldn't be loaded" notice — attention/info treatment with
 * an icon and a single `Try again`, NEVER red, never a toast, never per row.
 * The control is only rendered when a retry can actually work, which is why
 * this component is only ever handed a notice built by `laneFailureNotices`
 * (a `NOT_IMPLEMENTED` lane never produces one).
 *
 * The body's "Try again, or reload the page." is honest ONLY because this
 * control renders — the page (the sole consumer) always hands down its
 * memoized refetch. If a retry ever stops being available here, the copy
 * must change with it (`RETRY_INSTRUCTION` in `utils/patientCardLanes.js`).
 */
function FailureNotice({ notice, onRetry }) {
  return (
    <div
      data-testid={`lane-failure-notice-${notice.key}`}
      className="mb-3 rounded-md border border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2.5"
    >
      <p className="flex items-center gap-1.5 text-xs font-bold text-amber-900 dark:text-amber-100">
        <span aria-hidden="true">ℹ️</span>
        {notice.heading}
      </p>
      <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-200">{notice.body}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          data-testid={`lane-failure-retry-${notice.key}`}
          className="mt-2 rounded-md border border-slate-400 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1 text-[11px] font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          Try again
        </button>
      )}
    </div>
  );
}

/** A lane that is not built yet — info treatment, and NO retry: retrying
 *  could never succeed, so offering the control would be a lie. */
function NotBuiltNotice({ testId }) {
  return (
    <div
      data-testid={`lane-not-built-${testId}`}
      className="mb-3 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 px-3 py-2.5"
    >
      <p className="text-xs font-bold text-slate-800 dark:text-slate-100">{NOT_BUILT_HEADING}</p>
      <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-300">{NOT_BUILT_BODY}</p>
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

function toTimestamp(occurredAt) {
  if (!occurredAt) return null;
  const t = Date.parse(occurredAt);
  return Number.isNaN(t) ? null : t;
}

/**
 * Sort strictly by the server's `occurred_at` (INV-3 — never the GSI SK,
 * never array/insertion order). A row whose `occurred_at` won't parse sinks
 * to the bottom rather than throwing or being dropped — the contract says
 * `timeline` entries always carry a non-null `occurred_at` (R1-6), but this
 * stays defensive rather than assuming every row sorts.
 */
function sortByOccurredAtDesc(refs) {
  return [...refs].sort((a, b) => {
    const ta = toTimestamp(a.occurred_at);
    const tb = toTimestamp(b.occurred_at);
    if (ta == null && tb == null) return 0;
    if (ta == null) return 1;
    if (tb == null) return -1;
    if (tb !== ta) return tb - ta;
    // Deterministic tiebreak mirroring the server's (occurred_at, kind, id).
    if (a.kind !== b.kind) return a.kind < b.kind ? -1 : 1;
    return String(a.id) < String(b.id) ? -1 : 1;
  });
}

// Only ADMISSION is IPD — every other TimelineRef kind (VISIT, and the
// finer-grained LAB_ORDER/PLAN/SITTING/PRESCRIPTION kinds the contract
// reserves) is an outpatient-context artefact for the purpose of this
// binary All/OPD/IPD filter.
function bucketForKind(kind) {
  return kind === TIMELINE_KINDS.ADMISSION ? 'IPD' : 'OPD';
}

const ID_FIELD_BY_KIND = {
  [TIMELINE_KINDS.VISIT]: 'visit_id',
  [TIMELINE_KINDS.ADMISSION]: 'admission_id',
};

/** Resolves a TimelineRef to its full item from `sections[ref.ref.section]`.
 * Returns null (never throws) when the section wasn't requested (R1-4), the
 * lane has no items, or the kind isn't one this component resolves in
 * detail — the row still renders, generically, from the ref alone. */
function resolveItem(ref, sections) {
  const idField = ID_FIELD_BY_KIND[ref.kind];
  if (!idField) return null;
  const { items } = readLane(sections?.[ref.ref?.section]);
  return items.find((it) => it?.[idField] === ref.ref?.id) || null;
}

function resolveTimelineRows(timelineSection, sections) {
  const { items: refs } = readLane(timelineSection);
  return sortByOccurredAtDesc(refs).map((ref) => ({
    ref,
    bucket: bucketForKind(ref.kind),
    item: resolveItem(ref, sections),
  }));
}

/**
 * Groups already-sorted rows by year, using the ref's `day_ist` (the
 * calendar day a real bucket belongs to) — never a slice of the `occurred_at`
 * timestamp string, which mis-buckets anything between IST midnight and
 * 05:30. A row with no `day_ist` (defensive — the contract's `TimelineRef`
 * always carries one) falls into an "Unknown" group, ordered last.
 */
function groupByYear(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const year = row.ref.day_ist ? row.ref.day_ist.slice(0, 4) : 'Unknown';
    if (!map.has(year)) map.set(year, []);
    map.get(year).push(row);
  });
  return [...map.entries()].sort(([a], [b]) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return b.localeCompare(a);
  });
}
